/**
 * IterationTracker - Visualizes iteration attempts within a pipeline stage
 * Shows iteration dots with decision indicators (accept/improve/rewrite)
 */

import React from 'react';

import { cn } from '@/utils/cn';

import './pipeline-animations.css';

export interface StageIteration {
  iteration_number: number;
  decision: 'accept' | 'improve' | 'rewrite';
  scores: {
    overall?: number;
    quality?: number;
    cringe?: number;
    insight?: number;
    [key: string]: number | undefined;
  };
  feedback: string;
  improved_elements: string[];
  preserved_elements: string[];
  persona_name?: string;
  persona_model?: string;
  is_best_attempt?: boolean;
}

interface IterationTrackerProps {
  iterations: StageIteration[];
  currentIteration: number;
  maxIterations: number;
  compact?: boolean;
  className?: string;
}

const IterationTracker: React.FC<IterationTrackerProps> = ({
  iterations,
  currentIteration,
  maxIterations,
  compact = false,
  className
}) => {
  const getIterationIcon = (decision?: 'accept' | 'improve' | 'rewrite') => {
    switch (decision) {
      case 'accept':
        return '✓';
      case 'improve':
        return '↻';
      case 'rewrite':
        return '⟲';
      default:
        return '○';
    }
  };

  const getIterationColor = (iteration: StageIteration | undefined, index: number) => {
    if (!iteration) {
      return 'bg-gray-200';
    }
    
    if (iteration.is_best_attempt) {
      return 'bg-yellow-400 ring-2 ring-yellow-600';
    }
    
    switch (iteration.decision) {
      case 'accept':
        return 'bg-green-500';
      case 'improve':
        return 'bg-yellow-500';
      case 'rewrite':
        return 'bg-red-500';
      default:
        return index < currentIteration ? 'bg-blue-500' : 'bg-gray-300';
    }
  };

  const getTooltipContent = (iteration: StageIteration) => {
    const score = iteration.scores.overall || iteration.scores.quality || 0;
    return `Iteration ${iteration.iteration_number}: ${iteration.decision} (score: ${score.toFixed(2)})
${iteration.feedback || ''}
${iteration.is_best_attempt ? '⭐ Best Attempt' : ''}`;
  };

  if (compact) {
    // Compact view - just dots
    return (
      <div className={cn('flex items-center space-x-1', className)}>
        {Array.from({ length: maxIterations }).map((_, index) => {
          const iteration = iterations.find(it => it.iteration_number === index + 1);
          const isActive = index + 1 === currentIteration;
          
          return (
            <div
              key={index}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-300',
                getIterationColor(iteration, index + 1),
                isActive && 'animate-pulse scale-125'
              )}
              title={iteration ? getTooltipContent(iteration) : `Iteration ${index + 1}`}
            />
          );
        })}
      </div>
    );
  }

  // Full view with details
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">
          Iteration {currentIteration} of {maxIterations}
        </span>
        {iterations.some(it => it.is_best_attempt) && (
          <span className="text-xs text-yellow-600 font-medium">
            ⭐ Using Best Attempt
          </span>
        )}
      </div>

      <div className="flex items-center space-x-2">
        {Array.from({ length: maxIterations }).map((_, index) => {
          const iteration = iterations.find(it => it.iteration_number === index + 1);
          const isActive = index + 1 === currentIteration;
          const isPending = index + 1 > currentIteration;
          
          return (
            <div key={index} className="relative group">
              {/* Iteration dot */}
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300',
                  getIterationColor(iteration, index + 1),
                  isActive && 'animate-pulse scale-110 shadow-lg',
                  isPending && 'opacity-50',
                  'cursor-pointer hover:scale-110'
                )}
              >
                <span className="text-white">
                  {iteration ? getIterationIcon(iteration.decision) : index + 1}
                </span>
              </div>

              {/* Connector line */}
              {index < maxIterations - 1 && (
                <div
                  className={cn(
                    'absolute left-8 top-4 w-4 h-0.5 transition-colors duration-300',
                    index + 1 < currentIteration ? 'bg-green-400' : 'bg-gray-300'
                  )}
                />
              )}

              {/* Hover tooltip */}
              {iteration && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  <div className="bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-pre-line max-w-xs">
                    {getTooltipContent(iteration)}
                    
                    {/* Score breakdown */}
                    {Object.entries(iteration.scores).length > 1 && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <div className="font-semibold mb-1">Scores:</div>
                        {Object.entries(iteration.scores).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="capitalize">{key.replace('_', ' ')}:</span>
                            <span>{value?.toFixed(2) || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Improvements */}
                    {iteration.improved_elements.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <div className="font-semibold mb-1">Improved:</div>
                        {iteration.improved_elements.map((element, i) => (
                          <div key={i}>• {element}</div>
                        ))}
                      </div>
                    )}

                    {/* Preserved */}
                    {iteration.preserved_elements.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <div className="font-semibold mb-1">Preserved:</div>
                        {iteration.preserved_elements.map((element, i) => (
                          <div key={i}>• {element}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Progress ring visualization */}
      <div className="relative w-16 h-16 mx-auto mt-4">
        <svg className="iteration-ring transform -rotate-90" width="64" height="64">
          <circle
            className="iteration-ring-background"
            cx="32"
            cy="32"
            r="28"
            strokeWidth="4"
            fill="none"
            stroke="#e5e7eb"
          />
          <circle
            className="iteration-ring-progress"
            cx="32"
            cy="32"
            r="28"
            strokeWidth="4"
            fill="none"
            stroke="url(#iteration-gradient)"
            strokeDasharray={`${2 * Math.PI * 28}`}
            strokeDashoffset={`${2 * Math.PI * 28 * (1 - currentIteration / maxIterations)}`}
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="iteration-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-gray-700">
            {currentIteration}/{maxIterations}
          </span>
        </div>
      </div>
    </div>
  );
};

export default IterationTracker;